import { useCallback, useState } from "react";
import type { AiReviewStatus, RequirementChecklistItem } from "@pericles/agent-orchestrator";
import { type ExecutionStatus } from "@pericles/shared";

import { getEntitySlug } from "./entitySlug";

type RequirementExecutionStatus = ExecutionStatus;

export interface RequirementGenerationResponse {
  requirementId: string;
  summary?: string;
  checklist?: RequirementChecklistItem[];
  updated?: boolean;
}

export interface GenerateRequirementOptions {
  dryRun?: boolean;
}

export async function generateRequirementWithAI(
  requirementId: string | number,
  options?: GenerateRequirementOptions,
): Promise<RequirementGenerationResponse> {
  const response = await fetch(`/api/agent/requirements/${encodeURIComponent(String(requirementId))}/generate`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ dryRun: options?.dryRun ?? false }),
  });

  const payload = (await response.json()) as RequirementGenerationResponse & { error?: string };

  if (!response.ok) {
    throw new Error(payload?.error ?? `Failed to generate requirement (status ${response.status})`);
  }

  return payload;
}

export function useGenerateRequirementWithAI() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const generate = useCallback(
    async (requirementId: string | number, options?: GenerateRequirementOptions) => {
      setIsLoading(true);
      setError(null);

      try {
        return await generateRequirementWithAI(requirementId, options);
      } catch (err) {
        const normalizedError = err instanceof Error ? err : new Error("Failed to generate requirement");
        setError(normalizedError);
        throw normalizedError;
      } finally {
        setIsLoading(false);
      }
    },
    [],
  );

  return { generate, isLoading, error };
}

export async function updateRequirementAiReviewStatus(
  requirementId: string | number,
  aiReviewStatus: AiReviewStatus,
): Promise<void> {
  const requirementEntity = getEntitySlug("Requirement");
  const response = await fetch(
    `/api/entities/${requirementEntity}/${encodeURIComponent(String(requirementId))}`,
    {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ aiReviewStatus }),
    },
  );

  const payload = (await response.json().catch(() => ({}))) as { error?: string };

  if (!response.ok) {
    const message = payload.error ?? `Failed to update AI review status (status ${response.status})`;
    throw new Error(message);
  }
}

export async function updateRequirementExecutionStatus(
  requirementId: string | number,
  executionStatus: RequirementExecutionStatus,
): Promise<void> {
  const requirementEntity = getEntitySlug("Requirement");
  const response = await fetch(`/api/entities/${requirementEntity}/${encodeURIComponent(String(requirementId))}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ generationStatus: executionStatus }),
  });

  const payload = (await response.json().catch(() => ({}))) as { error?: string };

  if (!response.ok) {
    const message = payload.error ?? `Failed to update execution status (status ${response.status})`;
    throw new Error(message);
  }
}
